import React from 'react';
import styled from 'styled-components';

const PageSection = styled.section`
  padding: 4rem 0;
  direction: rtl;
`;

const PageContainer = styled.div`
  max-width: 64rem;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) {
    padding: 0 1.5rem;
  }
  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.25rem;
  color: #374151;
  max-width: 48rem;
  margin: 0 auto;
  line-height: 1.6;
`;

const VideoContainer = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 10px 25px -3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 3rem;
`;

const VideoWrapper = styled.div`
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ContentCard = styled.div`
  background-color: ${props => props.alternate ? '#fff7ed' : 'white'};
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  ${props => !props.alternate && 'border-right: 4px solid #ea580c;'}
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 1rem;
`;

const CardText = styled.p`
  color: #374151;
  line-height: 1.625;
`;

const TopicsSection = styled.div`
  background-color: #ea580c;
  padding: 3rem;
  border-radius: 0.75rem;
  margin-bottom: 3rem;
`;

const TopicsTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: bold;
  color: white;
  margin-bottom: 2rem;
  text-align: center;
`;

const TopicsList = styled.div`
  display: grid;
  gap: 1rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TopicItem = styled.div`
  background-color: rgba(255, 255, 255, 0.1);
  padding: 1rem;
  border-radius: 0.5rem;
  color: #fed7aa;
  font-weight: 500;
  text-align: center;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    color: white;
  }
`;

const CallToAction = styled.div`
  background: linear-gradient(to bottom right, #fff7ed, #fed7aa);
  padding: 3rem;
  border-radius: 0.75rem;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 1.875rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 1rem;
`;

const CTAText = styled.p`
  font-size: 1.125rem;
  color: #374151;
  margin-bottom: 2rem;
  max-width: 32rem;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled.button`
  background-color: #ea580c;
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 0.5rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 1.125rem;
  
  &:hover {
    background-color: #c2410c;
  }
`;

const EpisodeHighlights = styled.div`
  background-color: #f9fafb;
  padding: 2rem;
  border-radius: 0.75rem;
  margin-bottom: 3rem;
`;

const HighlightsTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const HighlightsList = styled.ul`
  color: #374151;
  line-height: 1.625;
  
  li {
    margin-bottom: 0.75rem;
    position: relative;
    padding-right: 1.5rem;
    
    &:before {
      content: "🎧";
      position: absolute;
      right: 0;
      top: 0;
    }
  }
`;

// Podcast Page Component
const PodcastPage = () => {
  const topics = [
    "אימון נפשי",
    "תזונה מעשית",
    "בניית הרגלים",
    "ניהול מתח",
    "מוטיבציה יומית",
    "איזון עבודה-חיים",
    "שינוי התנהגותי",
    "כושר בבית",
    "תכנון מטרות"
  ];

  const highlights = [
    "טיפים מעשיים שאפשר ליישם מיד",
    "סיפורי הצלחה מלקוחות אמיתיים",
    "תשובות לשאלות נפוצות בתחום הכושר",
    "אורחים מומחים מתחומי הבריאות והרווחה",
    "מדריכים צעד אחר צעד לשינוי הרגלים",
    "חידושים ומחקרים חדשים בתחום הכושר"
  ];

  return (
    <PageSection>
      <PageContainer>
        <PageHeader>
          <PageTitle>הפודקאסט שלי - "שעת כושר"</PageTitle>
          <PageSubtitle>
            הצטרף אלינו למסע של למידה, השראה וגילוי הכלים הנכונים לשינוי אמיתי ומתמשך.
            כל פרק מביא תובנות חדשות ועצות מעשיות לבניית אורח חיים בריא ומאושר.
          </PageSubtitle>
        </PageHeader>

        <VideoContainer>
          <VideoWrapper>
            <iframe 
              src="https://www.youtube.com/embed/videoseries?si=8jvjVjnFmyA9Rjdt&amp;list=PLhiL1hr9GrYcdEusU00XPvJkOY5gTIR2C" 
              title="YouTube video player" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
              referrerPolicy="strict-origin-when-cross-origin" 
              allowFullScreen
            />
          </VideoWrapper>
        </VideoContainer>

        <ContentGrid>
          <ContentCard>
            <CardTitle>למה כדאי להקשיב?</CardTitle>
            <CardText>
              הפודקאסט שלנו הוא יותר מסתם תוכן - זה מקור השראה יומי לכל מי שמחפש לעשות שינוי אמיתי. 
              אנחנו מתמקדים בתובנות מעשיות, כלים פשוטים ואסטרטגיות מוכחות שיעזרו לך להתקדם 
              צעד אחר צעד במסע שלך לחיים בריאים יותר.
            </CardText>
          </ContentCard>

          <ContentCard alternate>
            <CardTitle>פורמט הפרקים</CardTitle>
            <CardText>
              כל פרק נמשך בין 20-40 דקות ומתאים לחיי היומיום העמוסים שלך. תוכל להקשיב בדרך לעבודה, 
              בזמן האימון או בכל רגע פנוי. התוכן מעוצב להיות נגיש, מעניין ורלוונטי לכל רמות הניסיון 
              והידע בתחום הכושר והבריאות.
            </CardText>
          </ContentCard>
        </ContentGrid>

        <EpisodeHighlights>
          <HighlightsTitle>מה תמצאו בפרקים שלנו</HighlightsTitle>
          <HighlightsList>
            {highlights.map((highlight, index) => (
              <li key={index}>{highlight}</li>
            ))}
          </HighlightsList>
        </EpisodeHighlights>

        <TopicsSection>
          <TopicsTitle>נושאים שאנחנו מכסים</TopicsTitle>
          <TopicsList>
            {topics.map((topic, index) => (
              <TopicItem key={index}>{topic}</TopicItem>
            ))}
          </TopicsList>
        </TopicsSection>

        <CallToAction>
          <CTATitle>מוכן להתחיל את השינוי?</CTATitle>
          <CTAText>
            הפודקאסט הוא רק ההתחלה. אם אתה מוכן לעבור מהקשבה לפעולה, 
            בוא נתחיל לעבוד יחד על השינוי האמיתי שאתה מחפש.
          </CTAText>
          <CTAButton>
            הזמן פגישת ייעוץ
          </CTAButton>
        </CallToAction>
      </PageContainer>
    </PageSection>
  );
};

export default PodcastPage;